import React, { useState, useEffect } from 'react';
import { fetchAllConfig, manageConfig } from '../services/configService';
import { T, inputCls, inputStyle, onFocus, onBlur, TableCard, TableBar, ActionBtn, Toast, PrimaryBtn } from './theme';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const CONFIG_TYPES = [
  { id: 'DONATION_TYPE', label: 'Donation Types' },
  { id: 'EXPENSE_CATEGORY', label: 'Expense Categories' },
  { id: 'PAYMENT_METHOD', label: 'Payment Methods' },
  { id: 'GOTHRAM_SURNAMES', label: 'Gothram & Surnames' },
];

const AdminConfigTab = () => {
  const [activeType, setActiveType] = useState('DONATION_TYPE');
  const [items, setItems] = useState([]);
  const [newValue, setNewValue] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const loadItems = async () => {
    const data = await fetchAllConfig(activeType);
    setItems(data);
  };

  useEffect(() => { if (activeType !== 'GOTHRAM_SURNAMES') { loadItems(); } setEditItem(null); setNewValue(''); }, [activeType]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newValue.trim()) return;
    if (items.some(i => i.configValue === newValue.trim())) { showMsg('Already exists', 'error'); return; }
    const res = await manageConfig({ action: 'ADD', configType: activeType, configValue: newValue.trim() });
    if (res.statusCode === 201) { showMsg('Added!', 'success'); setNewValue(''); loadItems(); }
    else showMsg(res.message || 'Failed', 'error');
  };

  const handleEdit = async (oldValue) => {
    if (!editItem?.newValue?.trim()) return;
    if (editItem.newValue.trim() === oldValue) { setEditItem(null); return; }
    const oldItem = items.find(i => i.configValue === oldValue);
    const delRes = await manageConfig({ action: 'DELETE', configType: activeType, configValue: oldValue });
    if (delRes.statusCode !== 200) { showMsg('Edit failed', 'error'); return; }
    const addRes = await manageConfig({ action: 'ADD', configType: activeType, configValue: editItem.newValue.trim() });
    if (addRes.statusCode === 201) {
      if (oldItem?.status === 'INACTIVE') await manageConfig({ action: 'UPDATE_STATUS', configType: activeType, configValue: editItem.newValue.trim(), status: 'INACTIVE' });
      showMsg('Updated!', 'success'); setEditItem(null); loadItems();
    } else showMsg(addRes.message || 'Failed', 'error');
  };

  const handleToggleStatus = async (configValue, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const res = await manageConfig({ action: 'UPDATE_STATUS', configType: activeType, configValue, status: newStatus });
    if (res.statusCode === 200) { showMsg(`Marked ${newStatus}`, 'success'); loadItems(); }
    else showMsg(res.message || 'Failed', 'error');
  };

  const handleDelete = async (configValue) => {
    if (!window.confirm(`Delete "${configValue}"?`)) return;
    const res = await manageConfig({ action: 'DELETE', configType: activeType, configValue });
    if (res.statusCode === 200) { showMsg('Deleted!', 'success'); loadItems(); }
    else showMsg(res.message || 'Failed', 'error');
  };

  const currentConfig = CONFIG_TYPES.find(c => c.id === activeType);

  return (
    <div>
      {/* Config Type Selector */}
      <div className="flex flex-wrap gap-1 mb-6 p-1 rounded-xl" style={{ backgroundColor: T.stripe, border: `1px solid ${T.border}` }}>
        {CONFIG_TYPES.map(ct => (
          <button key={ct.id} onClick={() => setActiveType(ct.id)}
            className="px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all"
            style={activeType === ct.id ? { backgroundColor: T.dark, color: '#FAF9F6' } : { color: T.muted, backgroundColor: 'transparent' }}
            onMouseEnter={e => { if (activeType !== ct.id) e.currentTarget.style.color = T.ink; }}
            onMouseLeave={e => { if (activeType !== ct.id) e.currentTarget.style.color = T.muted; }}>
            {ct.label}
          </button>
        ))}
      </div>

      {activeType === 'GOTHRAM_SURNAMES' ? <GothramSurnamesSection /> : (<>

      {/* Add Form */}
      <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-3 mb-6">
        <input type="text" value={newValue} onChange={e => setNewValue(e.target.value)}
          placeholder={`Add new ${currentConfig?.label?.slice(0, -1) || 'item'}...`}
          className={inputCls + ' flex-1'} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
        <PrimaryBtn type="submit">Add &rarr;</PrimaryBtn>
      </form>

      <TableCard>
          <TableBar className="table-bar-wrap">
            <p className="text-xs font-bold" style={{ color: T.muted }}>{items.length} item(s) in {currentConfig?.label}</p>
            <div className="flex flex-wrap gap-2 text-[11px]" style={{ color: T.muted }}>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: '#15803d' }}></span>Active</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: T.border }}></span>Inactive</span>
            </div>
          </TableBar>
          {items.length === 0
            ? <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No items configured yet.</p>
            : (
              <ul>
                {items.map((item, i) => (
                  <li key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-5 py-3 gap-2 transition-colors"
                    style={{ borderBottom: i < items.length - 1 ? `1px solid ${T.border}` : 'none', opacity: item.status === 'INACTIVE' ? 0.5 : 1, backgroundColor: T.bg }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = T.stripe}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = T.bg}>
                    <div className="flex items-center gap-3 flex-1">
                      {editItem?.configValue === item.configValue ? (
                        <input type="text" value={editItem.newValue} onChange={e => setEditItem({ ...editItem, newValue: e.target.value })}
                          className="px-3 py-1.5 rounded-lg text-sm focus:outline-none w-48" style={inputStyle} onFocus={onFocus} onBlur={onBlur} autoFocus />
                      ) : (
                        <span className="text-sm font-bold" style={{ color: T.ink }}>{item.configValue}</span>
                      )}
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest"
                        style={item.status === 'ACTIVE' ? { color: '#15803d', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0' } : { color: T.muted, backgroundColor: T.stripe, border: `1px solid ${T.border}` }}>
                        {item.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {editItem?.configValue === item.configValue ? (
                        <>
                          <ActionBtn onClick={() => handleEdit(item.configValue)} color="green">Save</ActionBtn>
                          <ActionBtn onClick={() => setEditItem(null)} color="muted">Cancel</ActionBtn>
                        </>
                      ) : (
                        <>
                          <ActionBtn onClick={() => setEditItem({ configValue: item.configValue, newValue: item.configValue })} color="gold">Edit</ActionBtn>
                          <ActionBtn onClick={() => handleToggleStatus(item.configValue, item.status)} color={item.status === 'ACTIVE' ? 'muted' : 'green'}>
                            {item.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                          </ActionBtn>
                          <ActionBtn onClick={() => handleDelete(item.configValue)} color="red">Delete</ActionBtn>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
      </TableCard>

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </>)}
    </div>
  );
};

/* --- Gothram & Surnames Section ----------------------------------------- */
const GothramSurnamesSection = () => {
  const [gothrams, setGothrams] = useState([]);
  const [selectedGothram, setSelectedGothram] = useState(null);
  const [surnames, setSurnames] = useState([]);
  const [newGothram, setNewGothram] = useState('');
  const [newSurname, setNewSurname] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const loadGothrams = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list_all_gothrams' })
      });
      const data = await res.json();
      setGothrams(data.gothrams || []);
    } catch { setGothrams([]); }
    setLoading(false);
  };

  const loadSurnames = async (gothram) => {
    try {
      const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list_surnames', gothram })
      });
      const data = await res.json();
      setSurnames(data.surnames || []);
    } catch { setSurnames([]); }
  };

  useEffect(() => { loadGothrams(); }, []);

  useEffect(() => {
    if (selectedGothram) loadSurnames(selectedGothram);
    else setSurnames([]);
  }, [selectedGothram]);

  const handleAddGothramSurname = async (e) => {
    e.preventDefault();
    const g = newGothram.trim();
    const s = newSurname.trim();
    if (!g || !s) { showMsg('Both Gothram and Surname are required', 'error'); return; }
    try {
      const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add', gothram: g, surname: s })
      });
      const data = await res.json();
      if (res.ok) {
        showMsg(`Added ${s} under ${g}`, 'success');
        setNewGothram(''); setNewSurname('');
        loadGothrams();
        if (selectedGothram === g) loadSurnames(g);
      } else showMsg(data.message || 'Failed', 'error');
    } catch { showMsg('Error connecting to server', 'error'); }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch(`${API_BASE}/vrv-gothram-surname-validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', gothram: selectedGothram, surname: deleteConfirm })
      });
      const data = await res.json();
      if (res.ok) { showMsg('Deleted!', 'success'); loadSurnames(selectedGothram); loadGothrams(); }
      else showMsg(data.message || 'Failed', 'error');
    } catch { showMsg('Error connecting to server', 'error'); }
    setDeleteConfirm(null);
  };

  return (
    <div>
      {/* Add Form */}
      <form onSubmit={handleAddGothramSurname} className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <input type="text" value={newGothram} onChange={e => setNewGothram(e.target.value)}
            placeholder="Gothram" className={inputCls + ' w-full pr-8'} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
          {newGothram && (
            <button type="button" onClick={() => setNewGothram('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full transition-colors"
              style={{ color: T.muted, background: 'rgba(0,0,0,0.06)' }}
              onMouseEnter={e => e.currentTarget.style.color = T.ink}
              onMouseLeave={e => e.currentTarget.style.color = T.muted}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" d="M6 6l12 12M6 18L18 6"/></svg>
            </button>
          )}
        </div>
        <input type="text" value={newSurname} onChange={e => setNewSurname(e.target.value)}
          placeholder="Surname" className={inputCls + ' flex-1'} style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
        <PrimaryBtn type="submit">Add &rarr;</PrimaryBtn>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Gothram List */}
        <TableCard>
          <TableBar><p className="text-xs font-bold" style={{ color: T.muted }}>{gothrams.length} Gothram(s)</p></TableBar>
          {loading ? <p className="text-center py-6 text-sm" style={{ color: T.muted }}>Loading...</p> : (
            <ul style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {gothrams.map((g, i) => (
                <li key={g} onClick={() => { setSelectedGothram(g); setNewGothram(g); }}
                  className="px-5 py-3 text-sm font-bold cursor-pointer transition-colors"
                  style={{
                    borderBottom: i < gothrams.length - 1 ? `1px solid ${T.border}` : 'none',
                    color: selectedGothram === g ? T.gold : T.ink,
                    background: selectedGothram === g ? 'rgba(212,175,55,0.06)' : T.bg
                  }}
                  onMouseEnter={e => { if (selectedGothram !== g) e.currentTarget.style.background = T.stripe; }}
                  onMouseLeave={e => { if (selectedGothram !== g) e.currentTarget.style.background = T.bg; }}>
                  {g}
                </li>
              ))}
              {gothrams.length === 0 && <p className="text-center py-6 text-sm" style={{ color: T.muted }}>No gothrams added yet.</p>}
            </ul>
          )}
        </TableCard>

        {/* Surname List */}
        <div className="lg:col-span-2">
          <TableCard>
            <TableBar>
              <p className="text-xs font-bold" style={{ color: T.muted }}>
                {selectedGothram ? `${surnames.length} Surname(s) in ${selectedGothram}` : 'Select a Gothram'}
              </p>
            </TableBar>
            {!selectedGothram ? (
              <p className="text-center py-8 text-sm" style={{ color: T.muted }}>&larr; Select a gothram to view surnames</p>
            ) : surnames.length === 0 ? (
              <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No surnames under this gothram.</p>
            ) : (
              <ul style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {surnames.map((s, i) => (
                  <li key={s} className="flex items-center justify-between px-5 py-3 transition-colors"
                    style={{ borderBottom: i < surnames.length - 1 ? `1px solid ${T.border}` : 'none', background: T.bg }}
                    onMouseEnter={e => e.currentTarget.style.background = T.stripe}
                    onMouseLeave={e => e.currentTarget.style.background = T.bg}>
                    <span className="text-sm font-bold" style={{ color: T.ink }}>{s}</span>
                    <ActionBtn onClick={() => setDeleteConfirm(s)} color="red">Delete</ActionBtn>
                  </li>
                ))}
              </ul>
            )}
          </TableCard>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-sm mx-4 rounded-2xl overflow-hidden" style={{ background: 'white', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #0F172A, #16213E)', borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
              <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: 'rgba(212,175,55,0.7)' }}>Confirm Delete</p>
              <h3 className="text-base font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Remove Surname</h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm" style={{ color: '#374151', fontFamily: 'Inter, sans-serif' }}>
                Are you sure you want to delete <strong>"{deleteConfirm}"</strong> from <strong>"{selectedGothram}"</strong>?
              </p>
            </div>
            <div className="px-6 pb-5 flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
                style={{ border: `1.5px solid ${T.border}`, color: T.muted, background: 'transparent' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = T.ink; e.currentTarget.style.color = T.ink; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
                Cancel
              </button>
              <button onClick={confirmDelete}
                className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors"
                style={{ background: '#EF4444', color: 'white', border: '1.5px solid #EF4444' }}
                onMouseEnter={e => e.currentTarget.style.background = '#DC2626'}
                onMouseLeave={e => e.currentTarget.style.background = '#EF4444'}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default AdminConfigTab;
